import {
  periscopeThemeKey,
  periscopeThemePreferenceKey,
} from "../constants/index";

export type ThemePreference = "light" | "dark";

const isThemePreference = (value: string | null): value is ThemePreference =>
  value === "light" || value === "dark";

export const systemPrefersDark = (): boolean =>
  window.matchMedia("(prefers-color-scheme: dark)").matches;

export const resolveIsDarkMode = (preference: ThemePreference): boolean =>
  preference === "dark";

export const getStoredThemePreference = (): ThemePreference => {
  const stored = window.localStorage.getItem(periscopeThemePreferenceKey);
  if (isThemePreference(stored)) return stored;

  // No preference chosen yet under the new key: fall back to the legacy
  // resolved-boolean value written by earlier versions of the app, so
  // existing users keep the theme they already had.
  const legacyIsDarkMode = window.localStorage.getItem(periscopeThemeKey);
  if (legacyIsDarkMode === "true") return "dark";
  if (legacyIsDarkMode === "false") return "light";

  // Brand-new user: follow the OS preference once, then persist it as an
  // explicit choice (there is no live "system" mode any more).
  return systemPrefersDark() ? "dark" : "light";
};

export const storeThemePreference = (preference: ThemePreference): void => {
  window.localStorage.setItem(periscopeThemePreferenceKey, preference);
  // Several other components still read this key directly as a resolved
  // "true"/"false" boolean for their own initial render, so keep it in sync.
  window.localStorage.setItem(
    periscopeThemeKey,
    `${resolveIsDarkMode(preference)}`,
  );
};
